import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, Zap, ClipboardCheck, ShieldAlert, Wrench, Layers,
  Activity, TrendingUp, Clock, Package, X as XIcon, Filter as FilterIcon,
  ChevronRight, Bell, BellOff, ExternalLink, Percent, Repeat, Gauge,
} from "lucide-react";
import {
  computeKpis, computeAlerts, computeRepeatFaults, computeOutstanding,
  computeCompliance, computeWearExtremes, computeAssetHealth,
  isOpenBreakdown, isCritical, inspectionIssueCount, toDate,
} from "@/lib/insightsIntel";

const ALERT_DISMISS_KEY = "sl_insights_alerts_dismissed_v1";

// ---------------------------------------------------------------------------
// Command KPI Strip
// ---------------------------------------------------------------------------
export function CommandKpiStrip({ data, assets, onDrill }) {
  const nav = useNavigate();
  const kpis = useMemo(() => computeKpis(data, assets), [data, assets]);

  const cards = [
    { key: "openDefects",       label: "Open Defects",       Icon: Wrench,         accent: "#ef4444" },
    { key: "criticalFaults",    label: "Critical Faults",    Icon: ShieldAlert,    accent: "#dc2626" },
    { key: "assetsOffline",     label: "Assets Offline",     Icon: Layers,         accent: "#f97316" },
    { key: "inspectionsToday",  label: "Inspections Today",  Icon: ClipboardCheck, accent: "#22c55e" },
    { key: "breakdownHours",    label: "Breakdown Hours",    Icon: Clock,          accent: "#facc15" },
    { key: "outstandingRepairs", label: "Outstanding Repairs", Icon: Package,      accent: "#f58220" },
  ];

  const handleClick = (e, card) => {
    // Shift/Cmd-click → jump to filtered page. Plain click → drill drawer.
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      nav(kpis[card.key].href);
      return;
    }
    onDrill?.({ kind: "kpi", card, records: kpis[card.key].records });
  };

  return (
    <section
      className="grid gap-3 mb-6"
      style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
      data-testid="insights-kpi-strip"
    >
      {cards.map((c) => {
        const k = kpis[c.key];
        return (
          <button
            key={c.key}
            type="button"
            onClick={(e) => handleClick(e, c)}
            className="text-left panel-heavy p-4 group hover:-translate-y-0.5 transition-transform"
            style={{
              background: "linear-gradient(160deg, rgba(20,20,20,0.9) 0%, rgba(10,10,10,0.95) 100%)",
              border: "1px solid rgba(255,255,255,0.06)",
              boxShadow: `0 10px 30px -12px ${c.accent}44, inset 0 1px 0 rgba(255,255,255,0.03)`,
            }}
            data-testid={`kpi-card-${c.key}`}
            title="Click to drill in · Shift-click to jump to the filtered page"
          >
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: `${c.accent}14`, border: `1px solid ${c.accent}44`, color: c.accent }}
              >
                <c.Icon className="w-4 h-4" />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted font-black flex-1">{c.label}</div>
              <ChevronRight className="w-3.5 h-3.5 text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="mt-2 flex items-baseline gap-1">
              <span
                className="font-display font-black tabular-nums leading-none"
                style={{ color: c.accent, fontSize: 36, letterSpacing: "-0.03em" }}
              >
                {k.value}
              </span>
              {k.unit && <span className="text-sm text-muted font-black">{k.unit}</span>}
            </div>
            <div className="text-[11px] text-muted font-mono-tight mt-1 truncate" title={k.subtitle}>
              {k.subtitle}
            </div>
          </button>
        );
      })}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Alert Banner
// ---------------------------------------------------------------------------
export function AlertBanner({ data, assets, onDrillAsset, onDrillRecord }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(ALERT_DISMISS_KEY) || "[]")); }
    catch { return new Set(); }
  });
  const [showDismissed, setShowDismissed] = useState(false);
  const alerts = useMemo(() => computeAlerts(data, assets), [data, assets]);

  useEffect(() => {
    // Auto-clean: dismissed alerts whose underlying id no longer appears in the
    // computed set are forgotten so the localStorage set doesn't grow forever.
    const live = new Set(alerts.map((a) => a.id));
    let changed = false;
    const next = new Set();
    for (const id of dismissed) {
      if (live.has(id)) { next.add(id); }
      else changed = true;
    }
    if (changed) {
      setDismissed(next);
      try { localStorage.setItem(ALERT_DISMISS_KEY, JSON.stringify([...next])); } catch { /* no-op */ }
    }
  }, [alerts, dismissed]);

  const dismiss = (id) => {
    const next = new Set(dismissed); next.add(id);
    setDismissed(next);
    try { localStorage.setItem(ALERT_DISMISS_KEY, JSON.stringify([...next])); } catch { /* no-op */ }
  };
  const revive = (id) => {
    const next = new Set(dismissed); next.delete(id);
    setDismissed(next);
    try { localStorage.setItem(ALERT_DISMISS_KEY, JSON.stringify([...next])); } catch { /* no-op */ }
  };

  const visible = alerts.filter((a) => !dismissed.has(a.id));
  const hidden = alerts.filter((a) => dismissed.has(a.id));

  if (alerts.length === 0) return null;

  const levelColor = (l) => l === "critical"
    ? { bg: "rgba(220,38,38,0.10)", brd: "rgba(220,38,38,0.5)",  fg: "#ff9b9b", chip: "#dc2626" }
    : l === "high"
    ? { bg: "rgba(245,130,32,0.10)", brd: "rgba(245,130,32,0.45)", fg: "#ffd7ac", chip: "#f58220" }
    :   { bg: "rgba(250,204,21,0.08)", brd: "rgba(250,204,21,0.35)", fg: "#fde68a", chip: "#eab308" };

  const handleClick = (a) => {
    if (a.drill?.kind === "asset") { onDrillAsset?.(a.drill.id); return; }
    onDrillRecord?.(a.drill);
  };

  return (
    <section className="mb-6" data-testid="insights-alerts">
      <div className="flex items-center gap-2 mb-2">
        <Bell className="w-4 h-4" style={{ color: "var(--primary)" }} />
        <div className="text-[10px] uppercase tracking-widest font-black">Priority Alerts · {visible.length}</div>
        {hidden.length > 0 && (
          <button
            type="button"
            onClick={() => setShowDismissed((s) => !s)}
            className="ml-auto btn btn-xs btn-ghost"
            data-testid="alerts-toggle-dismissed"
          >
            <BellOff className="w-3 h-3" /> {showDismissed ? "Hide" : "Show"} dismissed · {hidden.length}
          </button>
        )}
      </div>
      <div className="grid gap-2">
        {(showDismissed ? [...visible, ...hidden] : visible).map((a) => {
          const c = levelColor(a.level);
          const isDismissed = dismissed.has(a.id);
          return (
            <div
              key={a.id}
              className="rounded-xl px-4 py-3 flex items-center gap-3"
              style={{ background: c.bg, border: `1px solid ${c.brd}`, opacity: isDismissed ? 0.55 : 1 }}
              data-testid={`alert-${a.level}-${a.id}`}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: c.chip, color: "#0a0a0a" }}
              >
                <AlertTriangle className="w-4 h-4" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-black tracking-tight" style={{ color: c.fg }}>
                  {a.title}
                </div>
                <div className="text-[11px] text-muted font-mono-tight truncate">{a.subtitle}</div>
              </div>
              <span
                className="pill !text-[10px] !py-0.5 !px-2 uppercase font-black tracking-widest"
                style={{ background: c.chip, color: "#0a0a0a" }}
              >
                {a.level}
              </span>
              <button
                type="button"
                className="btn btn-xs btn-ghost"
                onClick={() => handleClick(a)}
                data-testid={`alert-open-${a.id}`}
                title="Drill in"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
              {!isDismissed ? (
                <button
                  type="button"
                  className="btn btn-xs btn-ghost !px-1.5"
                  onClick={() => dismiss(a.id)}
                  title="Dismiss until this issue reappears"
                  data-testid={`alert-dismiss-${a.id}`}
                >
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-xs btn-ghost !px-1.5"
                  onClick={() => revive(a.id)}
                  title="Revive alert"
                  data-testid={`alert-revive-${a.id}`}
                >
                  <Bell className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Filter Bar
// ---------------------------------------------------------------------------
export function FilterBar({ data, assets, filters, onChange }) {
  const opts = useMemo(() => {
    const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort();
    const all = [
      ...(data.breakdowns || []),
      ...(data.inspections || []),
      ...(data.ppfs || []),
      ...(data.hazards || []),
      ...(data.handovers || []),
    ];
    return {
      assetTypes: uniq((assets || []).map((a) => a.asset_type || a.category)),
      assets:     uniq((assets || []).map((a) => a.id || a.asset_id)),
      crews:      uniq(all.map((r) => r.crew_name || r.crew || r.assigned_crew_name)),
      shifts:     uniq(all.map((r) => r.shift || r.from_shift)),
      supervisors:uniq(all.map((r) => r.supervisor_name || r.assigned_supervisor_name)),
      statuses:   uniq(all.map((r) => r.status)),
    };
  }, [data, assets]);

  const set = (k, v) => onChange({ ...filters, [k]: v || undefined });
  const clear = () => onChange({});

  const anyActive = Object.values(filters).some(Boolean);

  return (
    <section className="mb-6" data-testid="insights-filters">
      <div className="panel-heavy p-3 flex flex-wrap items-center gap-2">
        <FilterIcon className="w-4 h-4" style={{ color: "var(--primary)" }} />
        <div className="text-[10px] uppercase tracking-widest font-black">Filter</div>
        <Select label="Asset type" value={filters.assetType || ""} options={opts.assetTypes} onChange={(v) => set("assetType", v)} testid="filter-asset-type" />
        <Select label="Asset"      value={filters.assetId || ""}   options={opts.assets}     onChange={(v) => set("assetId", v)}   testid="filter-asset" />
        <Select label="Crew"       value={filters.crew || ""}      options={opts.crews}      onChange={(v) => set("crew", v)}      testid="filter-crew" />
        <Select label="Shift"      value={filters.shift || ""}     options={opts.shifts}     onChange={(v) => set("shift", v)}     testid="filter-shift" />
        <Select label="Supervisor" value={filters.supervisor || ""} options={opts.supervisors} onChange={(v) => set("supervisor", v)} testid="filter-supervisor" />
        <Select label="Status"     value={filters.status || ""}    options={opts.statuses}   onChange={(v) => set("status", v)}    testid="filter-status" />
        {anyActive && (
          <button className="btn btn-xs btn-ghost ml-auto" onClick={clear} data-testid="filter-clear">
            <XIcon className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
    </section>
  );
}
function Select({ label, value, options, onChange, testid }) {
  return (
    <label className="flex items-center gap-1.5 text-[11px] text-muted">
      <span className="uppercase tracking-widest font-black">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input !py-1 !text-[12px] !w-auto"
        data-testid={testid}
      >
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Repeat Fault widget
// ---------------------------------------------------------------------------
export function RepeatFaultWidget({ breakdowns, onDrillAsset }) {
  const [window, setWindow] = useState(30);
  const groups = useMemo(() => computeRepeatFaults(breakdowns, window), [breakdowns, window]);

  return (
    <section className="panel-heavy p-5" data-testid="insights-repeat-faults">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
             style={{ background: "rgba(239,68,68,0.14)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.4)" }}>
          <Repeat className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-muted font-black">Repeat Fault Tracking</div>
          <div className="font-display font-black text-lg leading-tight">Recurring issues across the fleet</div>
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setWindow(d)}
              className="text-[10.5px] uppercase tracking-widest font-black px-2 py-1 rounded-md"
              style={{
                background: window === d ? "var(--primary)" : "rgba(255,255,255,0.05)",
                color: window === d ? "#0a0a0a" : "var(--muted)",
                border: `1px solid ${window === d ? "var(--primary)" : "var(--border)"}`,
              }}
              data-testid={`repeat-window-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      {groups.length === 0 ? (
        <div className="text-sm text-muted italic">No fault has recurred on the same asset in this window. 🎯</div>
      ) : (
        <div className="grid gap-2">
          {groups.slice(0, 10).map((g) => (
            <button
              key={`${g.asset_id}-${g.signature}`}
              type="button"
              onClick={() => onDrillAsset?.(g.asset_id)}
              className="text-left rounded-lg p-3 flex items-center gap-3 hover:bg-white/[0.03] transition"
              style={{ border: "1px solid rgba(239,68,68,0.25)", background: "rgba(239,68,68,0.05)" }}
              data-testid={`repeat-row-${g.asset_id}`}
              title="Click to open the asset's full history"
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-black tabular-nums"
                   style={{ background: "#ef4444", color: "#0a0a0a", boxShadow: "0 0 0 3px rgba(239,68,68,0.2)" }}>
                {g.count}×
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-[14px] truncate" style={{ color: "var(--primary)" }}>{g.asset_id}</div>
                <div className="text-[12.5px] text-white/85 truncate">&quot;{g.headline}&quot;</div>
                <div className="text-[10.5px] text-muted font-mono-tight mt-0.5">
                  First: {g.firstAt?.toLocaleDateString() || "—"} · Last: {g.lastAt?.toLocaleDateString() || "—"}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted" />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Outstanding Work strip
// ---------------------------------------------------------------------------
export function OutstandingWorkStrip({ data, onNav }) {
  const o = useMemo(() => computeOutstanding(data), [data]);
  const cards = [
    { key: "defectsAwaitingRepair", label: "Defects awaiting repair", value: o.defectsAwaitingRepair, href: "/breakdowns", accent: "#ef4444" },
    { key: "reportsAwaiting",       label: "Reports awaiting approval", value: o.reportsAwaiting,   href: "/inspections", accent: "#f58220" },
    { key: "partsAwaiting",         label: "Parts awaiting order",    value: o.partsAwaiting,       href: "/ppfs",        accent: "#facc15" },
    { key: "tempRepairs",           label: "Temporary repairs",       value: o.tempRepairs,          href: "/breakdowns", accent: "#ff9330" },
    { key: "carriedOver",           label: "Carried-over issues",     value: o.carriedOver,          href: "/handovers",  accent: "#8bb4ff" },
  ];
  return (
    <section
      className="grid gap-2 mb-6"
      style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}
      data-testid="insights-outstanding"
    >
      {cards.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={() => onNav?.(c.href)}
          className="text-left panel p-3 hover:-translate-y-0.5 transition-transform"
          style={{ borderLeft: `3px solid ${c.accent}` }}
          data-testid={`outstanding-${c.key}`}
          title="Click to open the related list"
        >
          <div className="text-[9.5px] uppercase tracking-widest font-black text-muted">{c.label}</div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="font-display font-black tabular-nums" style={{ color: c.accent, fontSize: 26, lineHeight: 1 }}>{c.value}</span>
            <ChevronRight className="w-3 h-3 text-muted ml-auto" />
          </div>
        </button>
      ))}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Inspection Compliance
// ---------------------------------------------------------------------------
export function ComplianceWidget({ inspections, onNav }) {
  const c = useMemo(() => computeCompliance(inspections), [inspections]);
  const pct = c.total > 0 ? Math.round((c.onTime / c.total) * 100) : 100;
  return (
    <section className="panel-heavy p-5" data-testid="insights-compliance">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
             style={{ background: "rgba(34,197,94,0.14)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.4)" }}>
          <Percent className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-muted font-black">Inspection Compliance</div>
          <div className="font-display font-black text-lg leading-tight">{pct}% completed on time</div>
        </div>
      </div>
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}
      >
        <Stat label="On time"    value={c.onTime}    color="#22c55e" onClick={() => onNav?.("/inspections")} testid="compliance-ontime" />
        <Stat label="Overdue"    value={c.overdue}   color="#f58220" onClick={() => onNav?.("/inspections")} testid="compliance-overdue" />
        <Stat label="Drafts"     value={c.drafts}    color="#facc15" onClick={() => onNav?.("/inspections")} testid="compliance-drafts" />
        <Stat label="Issues found" value={c.issuesFound} color="#ef4444" onClick={() => onNav?.("/inspections")} testid="compliance-issues" />
      </div>
    </section>
  );
}
function Stat({ label, value, color, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left panel p-3 hover:-translate-y-0.5 transition-transform"
      style={{ borderTop: `2px solid ${color}` }}
      data-testid={testid}
    >
      <div className="text-[9.5px] uppercase tracking-widest font-black text-muted">{label}</div>
      <div className="font-display font-black tabular-nums mt-1" style={{ color, fontSize: 26, lineHeight: 1 }}>
        {value}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Wear Tracking
// ---------------------------------------------------------------------------
export function WearTrackerWidget({ assets, onDrillAsset }) {
  const { worst, approaching } = useMemo(() => computeWearExtremes(assets), [assets]);
  return (
    <section className="panel-heavy p-5" data-testid="insights-wear">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
             style={{ background: "rgba(245,130,32,0.14)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.4)" }}>
          <Gauge className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-muted font-black">Wear Tracking</div>
          <div className="font-display font-black text-lg leading-tight">Worst teeth · lip · wing shrouds</div>
        </div>
        <span className="pill !text-[10px] !py-0.5 !px-2 font-black uppercase tracking-widest"
              style={{ background: "rgba(255,255,255,0.05)", color: "var(--muted)" }}>
          {approaching.length} approaching
        </span>
      </div>
      {worst.length === 0 ? (
        <div className="text-sm text-muted italic">No wear data captured yet.</div>
      ) : (
        <div className="grid gap-2">
          {worst.map((w) => (
            <button
              key={`${w.asset_id}-${w.position}`}
              type="button"
              onClick={() => onDrillAsset?.(w.asset_id)}
              className="text-left rounded-lg p-3 flex items-center gap-3 hover:bg-white/[0.03] transition"
              style={{ border: "1px solid rgba(255,255,255,0.06)" }}
              data-testid={`wear-row-${w.asset_id}-${w.position}`}
            >
              <div className="w-11 h-11 rounded-lg flex items-center justify-center font-black tabular-nums"
                   style={{
                     background: w.pct >= 100 ? "#ef4444" : w.pct >= 75 ? "#f58220" : w.pct >= 50 ? "#facc15" : "#22c55e",
                     color: "#0a0a0a",
                   }}>
                {w.pct}%
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-[14px] truncate" style={{ color: "var(--primary)" }}>
                  {w.asset_id} <span className="text-white/70 font-mono-tight text-[11px]">{w.asset_name || ""}</span>
                </div>
                <div className="text-[12px] text-white/80 truncate">{w.position} <span className="text-muted">· {w.group.replace(/_/g, " ")}</span></div>
                {w.updated_at && (
                  <div className="text-[10.5px] text-muted font-mono-tight mt-0.5">
                    Updated {toDate(w.updated_at)?.toLocaleDateString() || "—"}
                    {w.updated_by && ` · ${w.updated_by}`}
                  </div>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-muted" />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Asset Health drill-in drawer
// ---------------------------------------------------------------------------
export function AssetHealthDrawer({ assetId, data, assets, onClose, onNav }) {
  const nav = useNavigate();
  const health = useMemo(() => computeAssetHealth(assetId, data, assets), [assetId, data, assets]);
  if (!assetId || !health) return null;
  const availColor = health.availability === "ready"
    ? { bg: "#22c55e", label: "Ready" }
    : health.availability === "at-risk"
    ? { bg: "#f58220", label: "At Risk" }
    : { bg: "#ef4444", label: "Down" };
  return (
    <div className="fixed inset-0 z-[60] flex" data-testid="asset-health-drawer">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }} onClick={onClose} />
      <aside
        className="ml-auto w-full max-w-3xl h-screen overflow-y-auto relative"
        style={{ background: "var(--surface)", borderLeft: "1px solid var(--border)" }}
      >
        <div className="p-5 sticky top-0 z-10" style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)" }}>
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted">Asset Health</div>
              <div className="font-display font-black tracking-tight text-4xl md:text-5xl leading-none mt-1"
                   style={{ color: "var(--primary)" }}>
                {assetId}
              </div>
              <div className="text-sm text-white/80 mt-1.5">
                {health.asset?.name || health.asset?.asset_name || "—"}
                {health.asset?.category && <> · <span className="text-muted">{health.asset.category}</span></>}
              </div>
              <span
                className="mt-2 inline-flex items-center gap-1 pill !text-[10px] !py-1 !px-2 uppercase font-black tracking-widest"
                style={{ background: availColor.bg, color: "#0a0a0a" }}
              >
                <Activity className="w-3 h-3" /> {availColor.label}
              </span>
            </div>
            <button className="btn btn-sm btn-ghost" onClick={onClose} data-testid="asset-health-close">
              <XIcon className="w-4 h-4" /> Close
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
            <Stat label="Open Defects"    value={health.openDefects.length}     color="#ef4444" onClick={() => nav("/breakdowns")} testid="ah-open-defects" />
            <Stat label="Critical"        value={health.criticalDefects.length} color="#dc2626" onClick={() => nav("/breakdowns")} testid="ah-critical" />
            <Stat label="Repeat faults"   value={health.repeatFaults.length}    color="#f58220" testid="ah-repeat" />
            <Stat label="Insp. Failures"  value={health.inspFailures.length}    color="#facc15" onClick={() => nav("/inspections")} testid="ah-insp-failures" />
          </div>
          <div className="text-[11px] text-muted font-mono-tight mt-3">
            Last inspection: <span className="text-white/85">{health.lastInspection?.toLocaleString() || "—"}</span>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {health.repeatFaults.length > 0 && (
            <div className="panel-heavy p-4">
              <div className="text-[10px] uppercase tracking-widest text-muted font-black mb-2">
                Repeat faults · {health.repeatFaults.length}
              </div>
              <div className="grid gap-1.5">
                {health.repeatFaults.map((g) => (
                  <div key={g.signature} className="rounded-lg p-2 flex items-center gap-3"
                       style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)" }}>
                    <span className="pill !text-[10px] !py-0.5 !px-2 font-black" style={{ background: "#ef4444", color: "#0a0a0a" }}>
                      {g.count}×
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-black truncate">&quot;{g.headline}&quot;</div>
                      <div className="text-[10.5px] text-muted font-mono-tight">
                        First {g.firstAt?.toLocaleDateString() || "—"} · Last {g.lastAt?.toLocaleDateString() || "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="panel-heavy p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted font-black mb-2">
              Timeline · {health.history.length} events
            </div>
            {health.history.length === 0 ? (
              <div className="text-sm text-muted italic">No history logged for this asset.</div>
            ) : (
              <ol className="relative border-l-2 pl-4 ml-2 space-y-2" style={{ borderColor: "var(--border)" }}>
                {health.history.map((e) => {
                  const cfg = e.kind === "inspection" ? { c: "#22c55e", L: "INS" }
                            : e.kind === "breakdown"  ? { c: e.critical ? "#dc2626" : "#ef4444", L: "BD" }
                            : e.kind === "ppf"        ? { c: "#facc15", L: "Plan Req" }
                            :                          { c: "#8bb4ff", L: "HAZ" };
                  return (
                    <li key={`${e.kind}-${e.id}`} className="relative" data-testid={`ah-timeline-${e.kind}-${e.id}`}>
                      <span className="absolute -left-[22px] top-1 w-3.5 h-3.5 rounded-full"
                            style={{ background: cfg.c, boxShadow: "0 0 0 3px var(--surface)" }} />
                      <div className="text-[10px] uppercase tracking-widest font-black" style={{ color: cfg.c }}>
                        {cfg.L} · {e.at?.toLocaleString() || "—"}
                      </div>
                      <div className="text-[13px] font-black leading-tight">{e.label}</div>
                      {e.status && <div className="text-[10.5px] text-muted font-mono-tight uppercase">{e.status}</div>}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic KPI records drawer (opened by the Command KPI Strip)
// ---------------------------------------------------------------------------
export function KpiDrawer({ open, onClose, onDrillAsset }) {
  if (!open) return null;
  const { card, records } = open;
  return (
    <div className="fixed inset-0 z-[60] flex" data-testid="kpi-drawer">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }} onClick={onClose} />
      <aside
        className="ml-auto w-full max-w-2xl h-screen overflow-y-auto relative"
        style={{ background: "var(--surface)", borderLeft: "1px solid var(--border)" }}
      >
        <div className="p-5 sticky top-0" style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)" }}>
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-muted">Drill-in</div>
              <div className="font-display font-black text-2xl tracking-tight">{card?.label}</div>
              <div className="text-sm text-muted mt-1">{records?.length} record{records?.length === 1 ? "" : "s"}</div>
            </div>
            <button className="btn btn-sm btn-ghost" onClick={onClose} data-testid="kpi-drawer-close">
              <XIcon className="w-4 h-4" /> Close
            </button>
          </div>
        </div>
        <div className="p-5 space-y-1.5">
          {(records || []).slice(0, 60).map((r, i) => {
            const label = r.complaint || r.cause || r.fault || r.description || r.template_name || r.summary || `${r.asset_id || "Asset"} · ${r.id || i}`;
            const critical = isCritical(r);
            const open = isOpenBreakdown(r);
            return (
              <button
                key={r.id || r._id || i}
                type="button"
                onClick={() => r.asset_id && onDrillAsset?.(r.asset_id)}
                className="w-full text-left rounded-lg p-3 flex items-center gap-3 hover:bg-white/[0.03] transition"
                style={{ border: "1px solid rgba(255,255,255,0.06)" }}
                data-testid={`kpi-record-${i}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-black text-[13px] truncate">
                    <span style={{ color: "var(--primary)" }}>{r.asset_id || "—"}</span>
                    <span className="text-muted"> · </span>
                    <span className="text-white/90">{String(label).slice(0, 90)}</span>
                  </div>
                  <div className="text-[10.5px] text-muted font-mono-tight">
                    {r.status && <span className="uppercase mr-2">{r.status}</span>}
                    {r.created_at && <span>{toDate(r.created_at)?.toLocaleString()}</span>}
                  </div>
                </div>
                {critical && (
                  <span className="pill !text-[10px] !py-0.5 !px-2 font-black uppercase" style={{ background: "#dc2626", color: "#0a0a0a" }}>Critical</span>
                )}
                {open && !critical && (
                  <span className="pill !text-[10px] !py-0.5 !px-2 font-black uppercase" style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.35)" }}>Open</span>
                )}
                <ChevronRight className="w-4 h-4 text-muted" />
              </button>
            );
          })}
          {(!records || records.length === 0) && (
            <div className="text-sm text-muted italic">Nothing in this bucket right now.</div>
          )}
        </div>
      </aside>
    </div>
  );
}
